import { graphql } from 'react-relay';
import { useEffect, useState } from 'react';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import type { GraphQLContentStatus } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { useCommentsData } from '../useCommentsData.tsx';
import { updateCommentsDataState } from '../helper/commentsDataHelper.ts';

import type {
	useActiveInlineCommentsQueryActiveInlineCommentsQuery as useActiveInlineCommentsQueryActiveInlineCommentsQuery$data,
	useActiveInlineCommentsQueryActiveInlineCommentsQueryVariables as useActiveInlineCommentsQueryActiveInlineCommentsQuery$variables,
} from './__types__/useActiveInlineCommentsQueryActiveInlineCommentsQuery.ts';
const useActiveInlineCommentsQueryActiveInlineCommentsQuery = graphql`
	query useActiveInlineCommentsQueryActiveInlineCommentsQuery(
		$inlineMarkerRefList: [String!]!
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus]
	) {
		comments(
			inlineMarkerRefList: $inlineMarkerRefList
			pageId: $pageId
			contentStatus: $contentStatus
		) {
			nodes {
				id
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					webui
					editui
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							resolved
							isDangling
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				version {
					by {
						displayName
					}
					when
					number
				}
				author {
					displayName
					profilePicture {
						path
					}
					permissionType
					... on KnownUser {
						accountId
					}
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							isDangling
							resolvedTime
							resolvedFriendlyDate
							resolvedUser
							resolved
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
				}
				createdAt {
					value
				}
				createdAtNonLocalized
				replies {
					id
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						webui
						editui
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineText
							inlineResolveProperties {
								resolved
								isDangling
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					version {
						by {
							displayName
						}
						when
						number
					}
					author {
						displayName
						profilePicture {
							path
						}
						permissionType
						... on KnownUser {
							accountId
						}
					}
					createdAt {
						value
					}
					createdAtNonLocalized
					reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
						ari
						containerAri
						reactionsCount
						reactionsSummaryForEmoji {
							id
							emojiId
							count
							reacted
						}
					}
				}
				reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
					ari
					containerAri
					reactionsCount
					reactionsSummaryForEmoji {
						id
						emojiId
						count
						reacted
					}
				}
			}
		}
	}
`;
export const COMMENT_BATCH_SIZE = 1000; // setting to 1000 for now until we implement infinite scroll for the comments panel

// gets comments using paginated query
export const useActiveInlineCommentsQuery = ({
	pageId,
	markerRefList,
	skip = false,
}: {
	pageId: string;
	markerRefList: string[];
	skip?: boolean;
}): {
	loading: boolean; // If we skip the query make sure loading is false
	data: useActiveInlineCommentsQueryActiveInlineCommentsQuery$data | undefined;
	error: ApolloErrorInterop | undefined;
	refetch: RefetchSupportedType<
		useActiveInlineCommentsQueryActiveInlineCommentsQuery$data,
		useActiveInlineCommentsQueryActiveInlineCommentsQuery$variables
	>;
} => {
	// The query loading was not sticking around long enough or triggering at the right times
	// so I created a loading state that will set after the query is successful/fails
	const [loading, setLoading] = useState(true);
	const [{ commentsDataMap }, { addNewCommentThreads, setCommentsDataMap }] = useCommentsData();

	const pageMode = useGetPageMode();
	const { userId: currentUserId } = useSessionData();

	// Determine if the query should be skipped
	const skipQuery = !pageId || markerRefList.length === 0 || skip;

	const hasAlreadyFetchedInitialData = Object.keys(commentsDataMap.inline).length > 0;

	// Execute the query
	const {
		data,
		loading: queryLoading,
		error,
		refetch,
	} = useQueryInterop<
		useActiveInlineCommentsQueryActiveInlineCommentsQuery$data,
		useActiveInlineCommentsQueryActiveInlineCommentsQuery$variables
	>(useActiveInlineCommentsQueryActiveInlineCommentsQuery, {
		variables: {
			pageId,
			inlineMarkerRefList: markerRefList,
			contentStatus: ['CURRENT', 'DRAFT'] as GraphQLContentStatus[],
		},
		// Skip is not respected after the initial call: https://github.com/apollographql/apollo-client/issues/6190#issuecomment-649933628
		fetchPolicy: skipQuery ? 'cache-only' : 'cache-and-network', // We want to utilize the cache as much as possible, but keep the list up to date if it changes
		skip: skipQuery,
		onCompleted: (result) => {
			updateCommentsDataState({
				data: result,
				updateData: hasAlreadyFetchedInitialData ? addNewCommentThreads : setCommentsDataMap,
				pageMode,
				currentUserId: currentUserId ?? '',
				isOpen: true,
			});
			setLoading(false);
		},
		onError: (err) => {
			// If the error is related to reactionsSummary returning null, just mark it handled
			// or if the error is related to cc-pages not being reachable, just mark it handled
			if (err.message.search(/reactions/i) !== -1 || err.message.search(/cc-pages/i) !== -1) {
				markErrorAsHandled(err);
			}
			setLoading(false);
		},
	});
	useEffect(() => {
		if (queryLoading) {
			setLoading(true);
		}
	}, [queryLoading]);

	return {
		loading: !skipQuery ? loading : false, // If we skip the query make sure loading is false
		data,
		error,
		refetch,
	};
};
